import { IFunding, JourneyType as JourneyTypeEnum } from '@bytel/leviator';
import type { ModesFinancement, ModesProduitFinancement } from '@bytel/pt-ihm-api-portailvente-sapic-catalogue';

import { CalculatedDiscounts, PrixAffiche } from '@app-types/api/vapi';
import { DiscountEnumType } from '@app-types/discount';
import { ObligationType } from '@app-types/plan';
import { ProductType } from '@app-types/product';

import { PLAY, TECHNOLOGY } from '@constants/provider';

/* eslint-disable @typescript-eslint/naming-convention */
export enum SapicConnectivity {
    '5G' = '5g',
    '4G' = '4g',
    '4G+' = '4g+',
}

export type SimulatedCartNeededElements = {
    alternatives: {
        typeCatalogue: string;
        categoriesComposantCommercial: string[];
        identifiant: string;
        libelle: string;
    }[];
};

export enum SapicCustomerCategory {
    GP = 'GP',
    PRO = 'PRO',
}

export enum SapicFundingMode {
    ALL = 'tous',
    AUTO = 'auto',
    MIN_ONE_OFF = 'min-one-off',
    NONE = 'aucun',
}

export enum SapicPriceForFilter {
    INITIAL = 'initial',
}

export enum SapicPriceForSort {
    INITIAL = 'initial',
}

export enum SapicCategoryId {
    PHONE = 'phone',
    ACCESSORY = 'accessory',
    PREMIUM = 'plan_premium',
    PREMIUM_PRO = 'plan_pro_premium',
    PREMIUM_RENEWAL = 'plan_premium_renew',
    SIMO = 'plan_simo',
    SIMO_PRO = 'plan_pro_simo',
    SOWO = 'plan_sowo',
    SOWO_RENEWAL = 'plan_sowo_renew',
    SOWO_SPECIALS = 'plan_sowo_specials',
    IDEO = 'plan_ideo',
    IDEO_PRO = 'plan_ideo_pro',
    FAIM = 'faim',
    FAI = 'fai',
    FAIM_UNLIMITED = 'faim_unlimited',
    FAMILY = 'family-web',
    SALES_PHONE = 'soldes_filter_telephones',
    SALES_ACCESSORY = 'soldes_filter_accessoires',
    HOTSPOT = 'device_faim',
    OPTIONS_DEFAULT = 'options_default',
}

export type SapicProductPayload = {
    categorie?: SapicCategoryId;
    clientCategorie?: SapicCustomerCategory;
    decalage?: string;
    detail?: string;
    filtre?: string;
    forfait?: string;
    gencodes?: string;
    idPu?: string;
    limite: string;
    modePourFinancement: SapicFundingMode;
    nom?: string;
    prix?: string;
    prixReferencePourFiltre?: SapicPriceForFilter;
    prixReferencePourTri?: SapicPriceForSort;
    tri?: string;
    typeParcours?: string;
    stock?: string;
};

export type SapicResponseType = {
    produits: SapicProductType[];
    initiateur: string;
    nombreTotalProduits: number;
};

export type SapicProductType = {
    ajoutsAutomatiquesFront?: string[];
    autonomie?: string;
    blocage?: string;
    categories: string[];
    categorieOption?: string;
    classification?: string;
    connectivite?: SapicConnectivity;
    couleurs?: string[] | null;
    dasMembre?: string;
    dasTete?: string;
    dasTronc?: string;
    debitAscendant?: string;
    debitDescendant?: string;
    delaisRenouvelabilite?: string;
    descriptionCourte?: string;
    dispoCnc?: boolean;
    dureeEngagement?: ObligationType;
    eligibilites?: string[];
    estCompatibleRenouvellement: boolean;
    estEnStock: boolean;
    estEssentiel?: boolean;
    estNonVendableAcquisition: boolean;
    estNonVendableRenouvellement: boolean;
    etat?: string;
    etiquetteAnimCo?: string;
    excluPro: boolean;
    sousType?: string;
    fraisDeLocation?: number;
    fraisDeMiseEnService?: Frais;
    fraisMiseADisposition?: Frais;
    gamme?: string;
    gammeCommerciale?: string;
    gammeNg?: string;
    gencode: string;
    image: string;
    indiceReparabilite?: string;
    jeu?: PLAY;
    libelleEnveloppeData?: string;
    libelleEnveloppeVoix?: string;
    libellePromo?: string;
    marque?: string;
    medias: string[];
    migrationPossible?: boolean;
    miniature?: string;
    modesDeFinancement: ModesProduitFinancement[];
    necessiteElements: SimulatedCartNeededElements[];
    niveauGammeConvergent?: string;
    nom: string;
    note: number;
    prix: {
        final: number;
        initial: number;
        pourToujours: number;
        subventionne: number;
    };
    produitPrioritaire?: string;
    promotions: Promotion[];
    promotionsApplicables?: Promotion[];
    quantite: number;
    recommande?: boolean;
    serieCommerciale?: string;
    simulation: SimulatedCartType;
    systemeExploitation?: string;
    technologie?: TECHNOLOGY;
    telephoneAvec?: string[];
    type: ProductType;
    type_id: ProductType;
    typeAccessoire?: string[];
    typeCategorieFai?: string;
    typeSim: string;
    url: string;
    partenaireCommercial?: string;
};

export type SimulatedCartType = {
    financements: {
        modesFinancement: ModesFinancement[];
        parcours: {
            elementsPanier: {
                composantCommercial: {
                    identifiant: string;
                };
                modesDeFinancementPanier: IFunding[];
                modesFinancementsElementPanier: ModesProduitFinancement[];
                referenceExterne: string;
            }[];
            referenceExterne: string;
        }[];
    };
    panier: {
        acheteur?: {
            type: 'NON_CONNU_GP' | 'PERSONNE' | 'NON_CONNU_PRO';
            noPersonne?: string;
        };
        parcours: JourneyType[];
    };
};

export type JourneyType = {
    referenceExterne: string;
    typeParcours: JourneyTypeEnum;
    contrat?: {
        identifiant: string;
    };
    statut: 'EN_COURS' | 'TERMINE';
    elements: {
        referenceExterne: string;
        prixAffiche?: PrixAffiche;
        promotionsCalculees?: CalculatedDiscounts;
        composantCommercial: {
            identifiant: string;
            typeCatalogue: 'BYTEL';
        };
    }[];
};

export type Frais = {
    prix: number;
    typePrix: string;
};

export type Promotion = {
    type: DiscountEnumType;
    valeur: number;
    reduction: number;
    estIncitation: boolean;
    identifiant: string;
    estDifferee: boolean;
    duree: number;
    proprietesSupplementaires: {
        fanion?: string;
        typesMarketing: string[];
    };
    typesAccess: string[];
    _links: {
        consulterPromotion: {
            type: string;
            href: string;
        };
    };
};

export type Financement = {
    type: string;
    prefere: boolean;
    apportInitial: number;
    nbrEcheances: number;
    montantMensualite: number;
    tAEG: number;
    tauxDebiteurFixe: number;
    montantInteret: number;
    coutTotalFinancement: number;
    montantAFinancer: number;
};

export type PromotionDetail = {
    identifiant: string;
    libelle: string;
    type: string;
    caracteristiques: {
        type: string;
        typeCaracteristique: string;
        valeur: string;
    }[];
    libelleCommercial: string;
    remise: {
        montant: number;
        type: string;
        estSousReserve: true;
    };
};

export type Prix = {
    initial: number;
    subventionne: number;
    pourToujours: number;
    final: number;
};

export type SapicTradeInRessourceAttribuee = {
    typeRessource: 'PARTENAIRE';
    caracteristiques: {
        type: 'CARACTERISTIQUE_SIMPLE';
        typeCaracteristique: string;
        valeur: string;
    }[];
};

export type SapicContextualisedProductPayload = {
    referenceExterneParcoursCible: string;
    panier: SimulatedCartType['panier'];
    ressources?: unknown[];
    contextualisation?: {
        ressourcesAttribuees?: SapicTradeInRessourceAttribuee[];
    };
};
